import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

interface RecapOpp {
  id: string;
  kind: string;
  title: string;
  organization: string | null;
  deadline_at: number | null;
  geo: string | null;
}

interface RecapPayload {
  generated_at: number;
  recent_saves: Array<RecapOpp & { saved_at: number; status: string }>;
  expiring: Array<RecapOpp & { saved_at: number; status: string }>;
  from_follows: Array<RecapOpp & { matched_kind: string | null; matched_geo: string | null }>;
}

/**
 * Quiet "This week" panel for the Opportunities page. Three small lists —
 * recent saves, expiring soon, new from follows — rendered as typographic
 * sub-sections, not cards. Hides entirely if all three are empty so the
 * surface stays empty by absence, not by placeholder.
 */
export function RecapPanel() {
  const api = useApi();
  const recap = useQuery({
    queryKey: ["opp-recap"],
    queryFn: () => api.get<RecapPayload>("/opportunities/recap"),
    staleTime: 60_000,
  });

  if (recap.isLoading || !recap.data) return null;

  const { recent_saves, expiring, from_follows } = recap.data;
  if (recent_saves.length === 0 && expiring.length === 0 && from_follows.length === 0) {
    return null;
  }

  return (
    <div className="panel p-7 space-y-6 mb-12">
      <p className="label-eyebrow">This week</p>

      {expiring.length > 0 && (
        <RecapList
          title="Closing soon"
          detail="Saved opportunities approaching their deadlines."
          items={expiring.map((o) => ({
            id: o.id,
            title: o.title,
            meta: o.deadline_at ? daysUntil(o.deadline_at) : null,
          }))}
        />
      )}

      {recent_saves.length > 0 && (
        <RecapList
          title="Recently saved"
          detail="What you've kept in the last seven days."
          items={recent_saves.map((o) => ({
            id: o.id,
            title: o.title,
            meta: relativeTimeAgo(o.saved_at),
          }))}
        />
      )}

      {from_follows.length > 0 && (
        <RecapList
          title="From your follows"
          detail="New opportunities matching what you follow."
          items={from_follows.map((o) => ({
            id: o.id,
            title: o.title,
            meta: o.matched_kind ?? o.matched_geo ?? null,
          }))}
        />
      )}
    </div>
  );
}

function RecapList({
  title,
  detail,
  items,
}: {
  title: string;
  detail: string;
  items: Array<{ id: string; title: string; meta: string | null }>;
}) {
  return (
    <section>
      <p className="font-display text-sm tracking-tight">{title}</p>
      <p className="mt-1 text-xs text-whisper leading-relaxed">{detail}</p>
      <ul className="mt-3 space-y-2">
        {items.map((o) => (
          <li
            key={o.id}
            className="flex items-baseline justify-between gap-4 text-sm"
          >
            <span className="text-fog leading-snug min-w-0 truncate">{o.title}</span>
            {o.meta && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted shrink-0">
                {o.meta}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function relativeTimeAgo(ts: number): string {
  const days = Math.round((Date.now() - ts) / (24 * 3600 * 1000));
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

function daysUntil(ts: number): string {
  const days = Math.ceil((ts - Date.now()) / (24 * 3600 * 1000));
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `${days} days`;
}
